import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';
import ar from './locales/ar.json';

export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const RTL_LANGUAGES: SupportedLanguage[] = ['ar'];

export function isRtl(lang: string): boolean {
    return RTL_LANGUAGES.includes(lang as SupportedLanguage);
}

/**
 * Keeps <html dir/lang> in sync with i18next's current language. This is
 * what actually drives RTL mirroring — Tailwind's rtl: variant and native
 * flexbox direction both key off the `dir` attribute, not off the language
 * itself, so every component that wants automatic mirroring needs this set
 * before it renders, not just the string content translated.
 */
function applyDirection(lang: string) {
    const dir = isRtl(lang) ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
}

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            en: { translation: en },
            ar: { translation: ar },
        },
        fallbackLng: 'en',
        supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
        interpolation: { escapeValue: false }, // React already escapes
        detection: {
            // localStorage first (explicit user choice persists across visits
            // and reloads), then browser language, so a first-time Arabic
            // browser gets Arabic by default without anyone having to toggle.
            order: ['localStorage', 'navigator'],
            caches: ['localStorage'],
            lookupLocalStorage: 'hrms_language',
        },
    });

applyDirection(i18n.language);
i18n.on('languageChanged', applyDirection);

export default i18n;
